import React, { useEffect, useState } from 'react';
import { Show } from '@refinedev/antd';
import { useShow } from '@refinedev/core';
import { Tag, theme, Card, Table, Button, Space, Spin } from 'antd';
import { LinkOutlined, FileImageOutlined } from '@ant-design/icons';
import { Field } from '../../components/Field';
import { API_BASE_URL } from '../../providers/dataProvider';

const STATUS_COLORS: Record<string, string> = {
  pending: '#fa8c16',
  processing: '#1890ff',
  completed: '#52c41a',
  failed: '#ff4d4f',
};

const tagStyle = (color: string) => ({
  borderColor: color,
  color,
  background: `${color}15`,
  borderRadius: 9999,
  padding: '2px 12px',
  fontWeight: 500,
});

function formatJson(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }
  return JSON.stringify(value, null, 2);
}

function extractImageUrl(item: any): string | undefined {
  const payload = item?.result?.result ?? item?.result;
  if (!payload) return undefined;
  if (payload.imageUrl) return payload.imageUrl;
  if (payload.items?.length > 0) {
    const meta = payload.items[0]?.entity?.metadata;
    if (meta?._imageUrl) return meta._imageUrl;
  }
  return undefined;
}

export const VisionQueueJobShow: React.FC = () => {
  const { token } = theme.useToken();
  const { queryResult } = useShow({ resource: 'vision-queue-jobs' });
  const item = queryResult?.data?.data as any;
  const jobId = item?.id;

  const [relatedLogs, setRelatedLogs] = useState<any[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);

  useEffect(() => {
    if (!jobId) return;
    const token = localStorage.getItem('access_token') || '';
    setLogsLoading(true);
    const url = `${API_BASE_URL}/admin/llm/call-logs?metadataKey=visionJobId&metadataValue=${encodeURIComponent(jobId)}&limit=20`;
    fetch(url, {
      headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
    })
      .then((r) => r.json())
      .then((data) => setRelatedLogs(data?.items || []))
      .catch((err) => {
        console.error('Failed to load related LLM logs:', err);
        setRelatedLogs([]);
      })
      .finally(() => setLogsLoading(false));
  }, [jobId]);

  const imageUrl = extractImageUrl(item);

  if (!item) {
    return <div style={{ color: token.colorTextSecondary }}>Loading...</div>;
  }

  return (
    <Show resource="vision-queue-jobs">
      <div style={{ padding: '8px 0' }}>
        <Field label="Job ID">
          <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{item.id}</span>
        </Field>
        <Field label="Type">{item.type || '—'}</Field>
        <Field label="Status">
          <Tag style={tagStyle(STATUS_COLORS[item.status] || '#898989')}>{item.status}</Tag>
        </Field>
        <Field label="User ID">
          <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{item.userId}</span>
        </Field>
        <Field label="Attempts Made">{item.attemptsMade ?? 0}</Field>
        <Field label="Created At">{item.createdAt ? new Date(item.createdAt).toLocaleString() : '—'}</Field>
        <Field label="Processed At">{item.processedAt ? new Date(item.processedAt).toLocaleString() : '—'}</Field>
        <Field label="Finished At">{item.finishedAt ? new Date(item.finishedAt).toLocaleString() : '—'}</Field>

        {item.failedReason && (
          <Field label="Error">
            <span style={{ color: '#ff4d4f' }}>{item.failedReason}</span>
          </Field>
        )}

        {imageUrl && (
          <Card title={<span><FileImageOutlined style={{ marginRight: 8 }} />Uploaded Image</span>} size="small" style={{ marginTop: 16 }}>
            <img
              src={imageUrl}
              alt="Vision job input"
              style={{ maxWidth: '100%', maxHeight: 320, objectFit: 'cover', borderRadius: 8, border: `1px solid ${token.colorBorderSecondary}` }}
            />
            <div style={{ marginTop: 8 }}>
              <a href={imageUrl} target="_blank" rel="noreferrer">
                <Button type="link" size="small" icon={<LinkOutlined />}>Open image</Button>
              </a>
            </div>
          </Card>
        )}

        <Card
          title={`Related LLM Call Logs (${relatedLogs.length})`}
          size="small"
          style={{ marginTop: 16 }}
        >
          {logsLoading ? (
            <Spin size="small" />
          ) : relatedLogs.length === 0 ? (
            <div style={{ color: token.colorTextSecondary }}>
              No LLM call logs found for this job. It may have used a cache hit or failed before reaching the LLM.
            </div>
          ) : (
            <Table
              dataSource={relatedLogs}
              rowKey="id"
              pagination={false}
              size="small"
              scroll={{ x: 900 }}
            >
              <Table.Column
                dataIndex="createdAt"
                title="Time"
                width={160}
                render={(date: string) => (date ? new Date(date).toLocaleString() : '—')}
              />
              <Table.Column dataIndex="promptName" title="Prompt" />
              <Table.Column dataIndex="model" title="Model" width={140} />
              <Table.Column
                dataIndex="status"
                title="Status"
                width={100}
                render={(status: string) => (
                  <Tag style={tagStyle(STATUS_COLORS[status.toLowerCase()] || '#898989')}>{status}</Tag>
                )}
              />
              <Table.Column
                dataIndex="latencyMs"
                title="Latency"
                width={100}
                render={(ms: number) => (ms !== undefined ? `${ms}ms` : '—')}
              />
              <Table.Column
                title="Actions"
                width={100}
                render={(_: any, record: any) => (
                  <Space>
                    <a href={`/llm-call-logs/show/${record.id}`}>
                      <Button type="link" size="small" icon={<LinkOutlined />}>View</Button>
                    </a>
                  </Space>
                )}
              />
            </Table>
          )}
        </Card>

        {item.result && (
          <Card title="Result" size="small" style={{ marginTop: 16 }}>
            <pre
              style={{
                fontSize: 12,
                maxHeight: 600,
                overflow: 'auto',
                background: token.colorBgLayout,
                padding: 12,
                borderRadius: 8,
                whiteSpace: 'pre-wrap',
              }}
            >
              {formatJson(item.result)}
            </pre>
          </Card>
        )}
      </div>
    </Show>
  );
};
